// Chuyển từ VUONGDATWOOD_V2/src/features/cong-luong/domain.ts

export const PAYROLL_STATUSES = ['NHAP', 'CHO_DUYET', 'TU_CHOI', 'DA_DUYET'];

export const PAYROLL_STATUS_LABELS = {
    NHAP: 'Nháp',
    CHO_DUYET: 'Chờ duyệt',
    TU_CHOI: 'Từ chối',
    DA_DUYET: 'Đã duyệt',
};

export const PAYROLL_VARIABLE_KINDS = ['NHAP_TAY', 'HE_THONG', 'THOA_THUAN', 'CONG_THUC'];

export const PAYROLL_VARIABLE_KIND_LABELS = {
    NHAP_TAY: 'Nhập tay',
    HE_THONG: 'Hệ thống',
    THOA_THUAN: 'Thỏa thuận',
    CONG_THUC: 'Công thức',
};

export const PAYROLL_VARIABLE_SOURCES = [
    'NANG_SUAT',
    'NHAP_KY',
    'THOA_THUAN',
    'PHEP_NAM',
    'NGAY_LE',
    'UNG_LUONG',
    'BHXH_THUE',
    'HE_THONG',
    'CONG_THUC',
];

export const PAYROLL_DATA_TYPES = ['SO', 'TIEN', 'TY_LE', 'TEXT'];

export const ADVANCE_STATUSES = ['CHUA_SU_DUNG', 'DA_SNAPSHOT', 'HUY'];

// Giá trị cột TRẠNG THÁI LÀM VIỆC của NHANSU; để trống được hiểu là đang làm.
export const EMPLOYEE_WORK_STATUSES = ['Đang làm', 'Tạm nghỉ', 'Đã nghỉ'];
export const DEFAULT_EMPLOYEE_WORK_STATUS = 'Đang làm';

export function getEmployeeWorkStatus(employee) {
    return employee?.['TRẠNG THÁI LÀM VIỆC'] || DEFAULT_EMPLOYEE_WORK_STATUS;
}

// Đơn vị mặc định suy từ kiểu dữ liệu, chỉ để hiển thị, không tham gia tính toán.
export function defaultPayrollUnit(dataType) {
    if (dataType === 'TIEN') return 'Đồng';
    if (dataType === 'TY_LE') return '%';
    return '';
}

export const PAYROLL_SEMANTIC_CODES = {
    gross: 'TC',
    advance: 'UNG',
    employeeInsurance: 'BH_NLD',
    personalIncomeTax: 'TNCN',
    net: 'TCHI',
};

// NC và GTC không được khai hằng số công ty: bước lấy nguồn còn đọc số chấm công thực tế làm
// phương án dự phòng, một mức chung sẽ đè nhầm lên số công của người chưa có số nhập tay.
const PAYROLL_CONSTANT_UNSAFE_MANUAL_CODES = new Set(['NC', 'GTC']);

// Biến nào được khai hằng số công ty (một giá trị cho mọi nhân sự, có hiệu lực theo ngày):
// biến Hệ thống nguồn Hệ thống chung, và biến Nhập tay trừ NC/GTC. Biến Thỏa thuận đã có giá
// trị riêng từng người, biến Công thức không có giá trị để khai.
export function canDeclarePayrollConstant(kind, source, code) {
    if (kind === 'HE_THONG' && source === 'HE_THONG') return true;
    if (kind === 'NHAP_TAY') return !PAYROLL_CONSTANT_UNSAFE_MANUAL_CODES.has(canonicalPayrollCode(code));
    return false;
}

export function isPayrollEditable(status) {
    return status === 'NHAP' || status === 'TU_CHOI';
}

export function assertPayrollEditable(status) {
    if (!isPayrollEditable(status)) {
        throw new Error('Kỳ lương không ở trạng thái cho phép chỉnh sửa.');
    }
}

export function getPayrollDateRange(month, year) {
    return {
        from: new Date(year, month - 1, 1),
        to: new Date(year, month, 0, 23, 59, 59, 999),
    };
}

export function formatPayrollCode(month, year) {
    return `CL-${year}-${String(month).padStart(2, '0')}`;
}

// Bí danh chỉ để đọc được dữ liệu và file Excel còn dùng mã dài kiểu cũ.
const PAYROLL_CODE_ALIASES = {
    BO_LONG: 'BL',
    TONG_KL: 'TKL',
    DON_GIA: 'DG',
    LUONG_SAN_PHAM: 'LSP',
    CONG: 'NC',
    DON_GIA_NGAY: 'DGN',
    LUONG_NGAY: 'LN',
    LUONG_KHOAN: 'LK',
    GIO_TANG_CA: 'GTC',
    DON_GIA_TANG_CA: 'DGTC',
    TIEN_TANG_CA: 'TTC',
    PC_COM: 'PCC',
    PC_TRACH_NHIEM: 'PCTN',
    PC_NHA_TRO: 'PCNT',
    PHU_CAP_KHAC: 'PCK',
    TRU_BO_LONG: 'TBL',
    TRU_UNG: 'UNG',
    TRU_BHXH: 'BH_NLD',
    GIAM_KHAC: 'GK',
    TONG_CONG: 'TC',
    THUC_CHI: 'TCHI',
};

export function canonicalPayrollCode(code) {
    const normalized = String(code ?? '').trim().toUpperCase();
    return PAYROLL_CODE_ALIASES[normalized] ?? normalized;
}

/**
 * Chọn dòng đang hiệu lực tại một thời điểm trong danh sách khai theo khoảng ngày (hằng số công
 * ty, công thức của biến). Khoảng ngày được phép chồng nhau; khi nhiều dòng cùng phủ một ngày thì
 * dòng cập nhật gần nhất thắng. Màn cấu hình và bảng lương phải dùng chung đúng luật này.
 */
export function resolveEffectiveDatedRow(rows, at = new Date()) {
    return rows
        .filter((row) => new Date(row.HIEU_LUC_TU) <= at && (!row.HIEU_LUC_DEN || new Date(row.HIEU_LUC_DEN) >= at))
        .sort((left, right) => new Date(right.UPDATED_AT).getTime() - new Date(left.UPDATED_AT).getTime())[0];
}
